import { FC, useRef } from 'react';
import { Flex, Typography } from 'antd';
import {
  SDropdown,
  ErrorMessage,
  SLabelTitle,
  SMailSelector,
  ModalDialog,
  StatusDropdown,
  SInput,
  SDatePicker,
  UploadButton,
} from '../..';
import { IProps } from './type';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DATE_FORMAT } from '../../../assets/conts';
import globalStyles from '../../../assets/styles/index.module.scss';
import styles from './index.module.scss';
import { useAppSelector } from '../../../store';
import { modalTypes } from '../../../assets/enums';
import { useSetOrganizationOnOpen } from '../../../hooks';

dayjs.extend(customParseFormat);

const TravelHealthInsuranceModal: FC<IProps> = (props) => {
  const { getFieldProps, setFieldValue, values, errors, touched } =
    props.formik;

  const uploadInput = useRef<HTMLInputElement | null>(null);

  const { organization } = useAppSelector(
    (state) => state.userReducer.userInfo,
  );

  useSetOrganizationOnOpen(props.open, values, setFieldValue);

  return (
    <ModalDialog
      title={props.title}
      subTitle={props.subTitle}
      open={props.open}
      handleOk={props.handleSubmit}
      handleCancel={props.handleCancel}
    >
      <Flex gap="middle" vertical>
        <Flex justify="space-between">
          <SLabelTitle>Şirket Bilgileri</SLabelTitle>
          <SDropdown
            id="organization"
            value={
              props.organizations.find(
                (item) =>
                  item.id ===
                  (values.organization ||
                    (organization !== 1 ? organization : undefined)),
              )?.name
            }
            placeholder="Şirket Seçiniz"
            className={globalStyles.width200px}
            disabled={organization !== 1}
            options={props.organizations.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
            onChange={(value) => setFieldValue('organization', value)}
          />
        </Flex>
        <Flex gap="small" vertical>
          <SLabelTitle>Kişi Bilgileri</SLabelTitle>
          <Flex gap="small">
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SInput placeholder="İsim" {...getFieldProps('first_name')} />
              {touched.first_name && errors.first_name && (
                <ErrorMessage>{errors.first_name}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SInput placeholder="Soyisim" {...getFieldProps('last_name')} />
              {touched.last_name && errors.last_name && (
                <ErrorMessage>{errors.last_name}</ErrorMessage>
              )}
            </Flex>
          </Flex>
          <Flex gap="small">
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SDatePicker
                id="birthday"
                name="birthday"
                value={
                  values.birthday ? dayjs(values.birthday, DATE_FORMAT) : null
                }
                placeholder="Doğum Tarihi"
                onChange={(date) => {
                  if (date) {
                    setFieldValue('birthday', date.format(DATE_FORMAT));
                  }
                }}
              />
              {touched.birthday && errors.birthday && (
                <ErrorMessage>{errors.birthday}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SInput
                placeholder="T.C. Kimlik No"
                {...getFieldProps('turkish_id')}
              />
              {touched.turkish_id && errors.turkish_id && (
                <ErrorMessage>{errors.turkish_id}</ErrorMessage>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex vertical gap="small">
          <SLabelTitle>Seyahat Bilgileri</SLabelTitle>
          <Typography className={styles.description}>
            * Seyahat sağlık sigortası teklifleriniz, ziyaret edeceğiniz ülkenin
            vize alma koşullarını sağlayacak mimimum teminat tutarı ile,
            belirttiğiniz gidiş tarihinden bir gün önce başlayıp dönüş
            tarihinden bir gün sonra sona erecek şekilde hazırlanacaktır.
          </Typography>
        </Flex>
        <UploadButton
          fileName={[...values.upload_files]?.map((item) => item.name)}
          uploadRef={uploadInput}
          onChange={props.handleUploadFile}
        />
        <Flex gap="small" vertical>
          <Flex justify="space-between" gap="small">
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SDatePicker
                id="travel_start_date"
                name="travel_start_date"
                value={
                  values.travel_start_date
                    ? dayjs(values.travel_start_date, DATE_FORMAT)
                    : null
                }
                placeholder="Başlangıç Tarihi"
                onChange={(date) => {
                  if (date) {
                    setFieldValue(
                      'travel_start_date',
                      date.format(DATE_FORMAT),
                    );
                  }
                }}
              />
              {touched.travel_start_date && errors.travel_start_date && (
                <ErrorMessage>{errors.travel_start_date}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SDatePicker
                id="travel_end_date"
                name="travel_end_date"
                value={
                  values.travel_end_date
                    ? dayjs(values.travel_end_date, DATE_FORMAT)
                    : null
                }
                placeholder="Bitiş Tarihi"
                onChange={(date) => {
                  if (date) {
                    setFieldValue('travel_end_date', date.format(DATE_FORMAT));
                  }
                }}
              />
              {touched.travel_end_date && errors.travel_end_date && (
                <ErrorMessage>{errors.travel_end_date}</ErrorMessage>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex vertical gap="small">
          <SDropdown
            id="countries"
            mode="multiple"
            value={values.countries}
            placeholder="Ülke"
            options={props.countries.map((c) => ({
              value: c.key,
              label: c.value,
            }))}
            onChange={(value) => setFieldValue('countries', value)}
          />
          {touched.countries && errors.countries && (
            <ErrorMessage>{errors.countries}</ErrorMessage>
          )}
        </Flex>
        {props.type === modalTypes.EDIT && (
          <Flex vertical gap="small">
            <SDropdown
              id="type"
              value={values.type}
              placeholder="Poliçe Tipi"
              options={props.travelHealthInsurancePolicyTypes.map((c) => ({
                value: c.key,
                label: c.value,
              }))}
              onChange={(value) => setFieldValue('type', value)}
            />
            {touched.type && errors.type && (
              <ErrorMessage>{errors.type}</ErrorMessage>
            )}
          </Flex>
        )}
        {props.type === modalTypes.EDIT && (
          <Flex vertical gap="small">
            <SInput
              placeholder="Poliçe No"
              {...getFieldProps('policy_number')}
            />
            {touched.policy_number && errors.policy_number && (
              <ErrorMessage>{errors.policy_number}</ErrorMessage>
            )}
          </Flex>
        )}
        {props.type === modalTypes.EDIT && organization === 1 && (
          <Flex justify="space-between">
            <SLabelTitle>Durum Bilgisi</SLabelTitle>
            <StatusDropdown
              status={values.recent_status}
              statusChoices={props.statusChoices}
              onChange={(value) => setFieldValue('recent_status', value)}
            />
          </Flex>
        )}
        {props.type === modalTypes.EDIT && (
          <SMailSelector
            value={values.email_users}
            placeholder="Mail Listesi"
            options={props.withViewPerm?.map((wvp) => ({
              value: wvp.value,
              label: wvp.label,
            }))}
            onChange={(value) => setFieldValue('email_users', value)}
          />
        )}
      </Flex>
    </ModalDialog>
  );
};

export default TravelHealthInsuranceModal;
